import { clearAcquisitionTouch } from './acquisition-touch'
import { accountReadiness, updateAccountReadiness } from './account-readiness'
import type { PostHog } from 'posthog-js'
import type { SupabaseClient } from '@supabase/supabase-js'
import { recordAnalyticsConsent } from '@/app/actions/consent'
import { accountAnalyticsConsent, type AccountAnalyticsConsent } from '@/lib/types/consent'
import { isNativePlatform } from '@/lib/utils/platform'
import {
  bannerAnswer,
  cacheAccountConsent,
  cachedAccountConsent,
  clearAccountConsentCache,
  declineAnalyticsOnThisBrowser,
  resolveAnalyticsMode,
  setAnalyticsMode,
  type AnalyticsMode,
} from './consent'

/*
 * Keeps PostHog's mode in step with the signed-in member's account answer on
 * usage analytics (Settings → Privacy & Consent), so the account and this
 * browser's cookie banner never say different things:
 *
 * - Signed in, the account decides (`resolveAnalyticsMode`), in the app and
 *   on the web. `<PostHogIdentify>` reads it on every page load and after
 *   sign-in, so a change made on another device lands at the next load.
 * - On the web, the banner answered while signed in is the account's answer:
 *   accepting records a yes, declining (or saving with analytics off) a no.
 *   Closing it records nothing.
 * - A banner yes stored on this browser before signing in is recorded as the
 *   account's yes the first time the member is signed in here, only if the
 *   account has no answer yet. It never overrides a no.
 * - An account no also turns this browser's stored banner yes into a no, so
 *   PostHog stays anonymous here after sign-out too. An account yes doesn't
 *   touch the banner: after sign-out the browser follows its own answer.
 * - The Settings switch applies on this device at once (`applyAccountAnswer`).
 */

/** The signed-in member, as `<PostHogIdentify>` last saw it. */
let signedInUserId: string | null | undefined = undefined

export function setSignedInUser(userId: string | null): void {
  const previousUser = signedInUserId ?? cachedAccountConsent()?.userId
  if (previousUser && previousUser !== userId) clearAcquisitionTouch()
  signedInUserId = userId
  updateAccountReadiness({ userId, resolved: false, identified: false })
}

/** The account's answer, or `null` when it can't be read (the mode stays as it is). */
export async function readAccountAnswer(
  supabase: Pick<SupabaseClient, 'from'>,
  userId: string
): Promise<AccountAnalyticsConsent | null> {
  try {
    const { data, error } = await supabase
      .from('consent_records')
      .select('granted, source, policy_version')
      .eq('user_id', userId)
      .eq('consent_type', 'analytics')
      .maybeSingle()
    if (error) return null
    return accountAnalyticsConsent(data)
  } catch {
    return null
  }
}

/** Applies an account answer on this device and returns the resulting mode. */
export function applyAccountAnswer(
  ph: PostHog,
  consent: AccountAnalyticsConsent,
  userId: string | null = signedInUserId ?? null
): AnalyticsMode {
  if (userId) cacheAccountConsent(userId, consent)
  if (consent === 'withdrawn') {
    declineAnalyticsOnThisBrowser()
    clearAcquisitionTouch()
  }
  const mode = resolveAnalyticsMode({
    native: isNativePlatform(),
    account: consent,
    banner: bannerAnswer(),
  })
  const ready = accountReadiness()
  if (ready.userId === userId && mode === 'anonymous')
    updateAccountReadiness({ ...ready, identified: false })
  setAnalyticsMode(ph, mode)
  return mode
}

/**
 * The account's answer, just read after load or sign-in. With no answer yet, a
 * web banner yes stored here is recorded as the account's (best effort: if the
 * write fails, the banner yes still holds on this browser and the next load
 * tries again).
 */
export function applySignedInAnswer(
  ph: PostHog,
  userId: string,
  consent: AccountAnalyticsConsent
): AnalyticsMode {
  const banner = bannerAnswer()
  if (consent === 'none' && !isNativePlatform() && banner.answered && banner.analytics) {
    void recordAnalyticsConsent(true).catch(() => undefined)
    return applyAccountAnswer(ph, 'granted', userId)
  }
  return applyAccountAnswer(ph, consent, userId)
}

/** Signed out: forget the account's answer; the banner (web) or nothing (app) decides. */
export function applySignedOut(ph: PostHog): AnalyticsMode {
  clearAccountConsentCache()
  const mode = resolveAnalyticsMode({
    native: isNativePlatform(),
    account: null,
    banner: bannerAnswer(),
  })
  setAnalyticsMode(ph, mode)
  return mode
}

/**
 * The web banner was answered (already stored). `decided` is false for the
 * close button, which is not an answer. Signed in, a real answer becomes the
 * account's; otherwise the rule is applied with what is known.
 */
export function applyBannerAnswer(
  ph: PostHog,
  analytics: boolean,
  decided: boolean
): AnalyticsMode {
  if (signedInUserId && decided) {
    void recordAnalyticsConsent(analytics).catch(() => undefined)
    return applyAccountAnswer(ph, analytics ? 'granted' : 'withdrawn')
  }
  const cached = cachedAccountConsent()
  const account = signedInUserId && cached?.userId === signedInUserId ? cached.consent : null
  const mode = resolveAnalyticsMode({ native: false, account, banner: bannerAnswer() })
  setAnalyticsMode(ph, mode)
  return mode
}
